/** Quality of the re-encoded JPEG: visually lossless for evidence photos. */
const JPEG_QUALITY = 0.9

export interface JpegImage {
  /** The JPEG bytes as bare base64, the form the capture edge functions take. */
  base64: string
  /** Size of the JPEG in bytes, for the backend's size limit. */
  bytes: number
}

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  // Chunked: spreading a multi-megabyte array into one call overflows the stack.
  for (let offset = 0; offset < buffer.length; offset += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(offset, offset + 0x8000))
  }
  return btoa(binary)
}

/**
 * Re-encode an image file as a JPEG in the browser.
 *
 * The app sends every capture as a JPEG, and the capture edge functions only
 * take JPEG, PNG and WebP; decoding through the browser and re-encoding lets an
 * operator pick any image the browser can open and still produce the capture
 * the app would.
 *
 * @throws if the browser cannot decode the file.
 */
export const imageToJpeg = async (file: File): Promise<JpegImage> => {
  const bitmap = await createImageBitmap(file)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const context = canvas.getContext('2d')
    if (context === null) {
      throw new Error('Canvas 2D context unavailable')
    }
    // JPEG has no alpha: paint transparent regions white rather than black.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0)

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        result => {
          if (result === null) {
            reject(new Error('JPEG encoding failed'))
          } else {
            resolve(result)
          }
        },
        'image/jpeg',
        JPEG_QUALITY,
      )
    })

    return { base64: await blobToBase64(blob), bytes: blob.size }
  } finally {
    bitmap.close()
  }
}
